import type { DownloadExportType, DownloadItem } from '@/models/download';

export const XLSX_MIME_TYPE =
   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const BYTES_PER_UNIT = 1024;
const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;
const SECONDS_PER_MINUTE = 60;

export const findOldestPendingDownload = (
   downloads: DownloadItem[],
   surveyId: string,
   exportType: DownloadExportType,
): DownloadItem | undefined =>
   downloads
      .filter(
         (download) =>
            download.surveyId === surveyId &&
            download.exportType === exportType &&
            download.status === 'inProgress',
      )
      .sort((first, second) => first.createdAt.localeCompare(second.createdAt))
      .at(0);

export const formatFileSize = (sizeInBytes: number): string => {
   if (sizeInBytes <= 0) return `0 ${FILE_SIZE_UNITS[0]}`;

   const unitIndex = Math.min(
      Math.floor(Math.log(sizeInBytes) / Math.log(BYTES_PER_UNIT)),
      FILE_SIZE_UNITS.length - 1,
   );
   const size = sizeInBytes / BYTES_PER_UNIT ** unitIndex;

   return `${size.toFixed(2)} ${FILE_SIZE_UNITS[unitIndex]}`;
};

export const formatDuration = (durationInSeconds: number): string => {
   const roundedSeconds = Math.max(0, Math.round(durationInSeconds));

   if (roundedSeconds < SECONDS_PER_MINUTE) return `${roundedSeconds}s`;

   const minutes = Math.floor(roundedSeconds / SECONDS_PER_MINUTE);
   const seconds = roundedSeconds % SECONDS_PER_MINUTE;

   return seconds > 0 ? `${minutes}min${seconds}` : `${minutes}min`;
};

export const getElapsedSeconds = (
   startedAtIso: string,
   nowTimestamp: number,
): number => Math.max(0, (nowTimestamp - Date.parse(startedAtIso)) / 1000);

const STARTED_STALL_TIMEOUT_MS = 2 * 60 * 1000;
const QUEUED_STALL_TIMEOUT_MS = 10 * 60 * 1000;

export const isDownloadStalled = (
   download: DownloadItem,
   nowTimestamp: number,
): boolean => {
   if (download.status !== 'inProgress') return false;

   const lastActivityTimestamp = Date.parse(
      download.lastActivityAt ?? download.createdAt,
   );
   const hasProgressStarted = download.totalCount !== undefined;
   const stallTimeoutMs = hasProgressStarted
      ? STARTED_STALL_TIMEOUT_MS
      : QUEUED_STALL_TIMEOUT_MS;

   return nowTimestamp - lastActivityTimestamp > stallTimeoutMs;
};
